import './style.css';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Banner, HelpIcon, LoadingIndicator } from '@thermon-platforms/thermon-ui-core';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import useDevicesStore from 'store/devices';
import useDeviceTypesStore from 'store/device-types';
import useLicenseStore from 'store/license';
import ConfigControl from 'components/config-control';
import { getConfigItem } from 'util/device';
import api from 'util/api';


function GetStarted() {
  const navigate = useNavigate();
  const [savingGateway, setSavingGateway] = useState(false);
  const [errors, setErrors] = useState();
  const [savingLicense, setSavingLicense] = useState(false);
  const [licenseError, setLicenseError] = useState();
  const [file, setFile] = useState();
  const [, devicesStoreActions] = useDevicesStore();
  const [deviceTypesStore] = useDeviceTypesStore();
  const [, licenseStoreActions] = useLicenseStore();
  const [page, setPage] = useState(1);
  const site = useRef({
    name: '',
    type: 'Site',
    coordinates: {},
    configuration: [
      {
        key: 'externalid',
        value: '0000000000000001'
      },
      {
        key: 'SiteId',
        value: 'site01'
      },
      {
        key: 'TemperatureUnitDisplay',
        value: 'degC'
      }
    ],
    children: []
  });
  const gatewayType = useRef();

  function validateSiteSettings() {
    if (!site.current.name) {
      setErrors({
        name: t`Name is required`
      });
      return;
    }

    if (!getConfigItem(site.current, 'SiteId').value) {
      setErrors({
        siteId: t`Site ID is required`
      });
      return;
    }

    next();
  }

  function validateGatewaySettings() {
    setSavingGateway(true);
    devicesStoreActions.addChild(site.current, {id: '00000000-0000-0000-0000-000000000000'})
      .then(() => {
        next();
      })
      .catch(err => {
        setErrors(err);
      })
      .finally(() => {
        setSavingGateway(false);
      });
  }

  function next() {
    setPage(page + 1);
  }

  function prev() {
    setPage(page - 1);
  }

  // On initial load, let's make sure the site has all the configuration items it needs
  useEffect(() => {
    gatewayType.current = deviceTypesStore.types.find(type => type.name == 'Site');
    gatewayType.current?.configuration?.forEach(section => {
      section.items.forEach(item => {
        const existing = site.current.configuration.find(i => i.key == item.key);
        if (!existing) {
          site.current.configuration.push({
            key: item.key,
            value: item.default
          });
        }
      });
    });
  }, [deviceTypesStore]);

  function handleUpload() {
    const formData = new FormData();
    formData.append('licenseFile', file, file.name);
    setSavingLicense(true);

    api(`/auth/license`, {
      method: 'POST',
      body: formData,
      headers: {
        // This is an awkward hack to remove the Content-Type
        // header which lets the browser select the correct
        // type for upload. Unfortunately, just setting it to
        // multipart/form-data; doesn't work
      }
    })
      .then(async res => {
        const expiry = await res.json();
        setFile(undefined);
        if (expiry > 0) {
          finish();
        } else {
          setLicenseError(t`License file is invalid or expired`);
        }
      })
      .catch(() => {
        setLicenseError(t`There was an error uploading the license file.`);
      })
      .finally(() => {
        setSavingLicense(false);
      });
  }

  function finish() {
    licenseStoreActions.query();
    navigate('/devices');
  }

  if (!deviceTypesStore.types.length) return (
    <div className="view get-started">
      <LoadingIndicator />
    </div>
  );

  return(<>
    {page == 1 ? <div className="view get-started">
      <header><h2><Trans>Get Started</Trans></h2></header>
      <section>
        <p>
          <Trans>
            Welcome to Thermon Egis! We need to configure a few things before getting started.
          </Trans>
        </p>
        <br />
        <ul className="form">
          <li>
            <label htmlFor="siteName"><Trans>Site Name</Trans></label>
            <input
              type="text"
              id="siteName"
              name="siteName"
              placeholder="Name"
              className={`${errors?.name ? 'error' : ''}`}
              defaultValue={site.current?.name}
              onChange={event => {
                delete errors?.name;
                setErrors(Object.assign({}, errors));
                site.current.name = event.target.value;
                getConfigItem(site.current, 'Name').value = event.target.value;
              }}
            />
            {errors?.name ? <p className="error-text">{errors.name}</p> : <></>}
          </li>
          <li>
            <label>
              <Trans>Site ID</Trans>
              <HelpIcon text={t`This is used for certain system actions and is not necessary to change.`} />
            </label>
            <input
              type="text"
              placeholder="Site ID"
              className={`${errors?.siteId ? 'error' : ''}`}
              defaultValue={getConfigItem(site.current, 'SiteId').value}
              onChange={event => {
                delete errors?.siteId;
                setErrors(Object.assign({}, errors));
                getConfigItem(site.current, 'SiteId').value = event.target.value;
              }}
            />
            {errors?.siteId ? <p className="error-text">{errors.siteId}</p> : <></>}
          </li>
          {/* <li className="coordinates optional">
            <label><Trans>Coordinates</Trans></label>
            <input
              type="number"
              placeholder="latitude"
              onChange={event => site.current.coordinates.latitude = event.target.value}
            />
            <input
              type="number"
              placeholder="longitude"
              onChange={event => site.current.coordinates.longitude = event.target.value}
            />
          </li> */}
          <li>
            <label>
              <Trans>Temperature Units</Trans>
              <HelpIcon text={t`Select the temperature unit for display across Thermon Egis`} />
            </label>
            <select
              defaultValue={getConfigItem(site.current, 'TemperatureUnitDisplay').value}
              onChange={event => getConfigItem(site.current, 'TemperatureUnitDisplay').value = event.target.value}
            >
              <option value="degC">°C</option>
              <option value="degF">°F</option>
            </select>
          </li>
          <li>
            <button className="primary" onClick={validateSiteSettings}>
              <Trans>Continue</Trans>
            </button>
          </li>
        </ul>
      </section>
    </div> :  null}
    {page == 2 ? <div className="view get-started">
      <header><h2><Trans>Gateway Settings</Trans></h2></header>
      <div className="configuration-section">
        <ul className="form">
          <li>
              <label>
                <Trans>Gateway ID</Trans>
              </label>
              <input
                type="text"
                placeholder="Gateway ID"
                className={`${errors?.GatewayId ? 'error' : ''}`}
                defaultValue={getConfigItem(site.current, 'GatewayId').value}
                onChange={event => {
                  delete errors?.GatewayId;
                  getConfigItem(site.current, 'GatewayId').value = event.target.value;
                }}
              />
              {errors?.GatewayId ? <p className="error-text">{errors.GatewayId}</p> : <></>}
            </li>
        </ul>
      </div>
      <section>
        {[].concat(gatewayType.current?.configuration || []).sort((a, b) => a.items[0]?.categoryOrder - b.items[0]?.categoryOrder)
          .map(section => {
            // These sections are either handled already or not relevant for new installs
            if (['Overview', 'Firmware', 'Site Settings'].includes(section.section)) return null;
            return (
              <div className="configuration-section" key={section.section}>
                <h4>{section.section}</h4>
                <ul className="form">
                  {section.items.map((item, i) => {
                    return (
                      <li key={`config-${section.section}-${i}`}>
                        <ConfigControl
                          item={item}
                          error={errors?.[item.key]}
                          onChange={val => {
                            getConfigItem(site.current, item.key).value = val
                            if (errors?.[item.key]) {
                              delete errors?.[item.key];
                              setErrors(Object.assign({}, errors));
                            }
                          }}
                          value={getConfigItem(site.current, item.key)}
                        />
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })
        }
        <ul className="form">
          <li>
            <button onClick={prev}><Trans>Previous</Trans></button>
            <button
              className={`primary ${savingGateway ? 'processing' : ''}`}
              onClick={validateGatewaySettings}
              disabled={savingGateway}
            >
              <Trans>Save</Trans>
            </button>
          </li>
        </ul>
      </section>
    </div> : null}
    {page == 3 ? <div className="view get-started">
      <header><h2><Trans>License Upload</Trans></h2></header>
      <section>
        <Banner status="alert" show={licenseError}>
          {licenseError}
        </Banner>
        <p>
          <Trans>
            Upload your license file to get started. If your install
            included a license or you still need to request one,
            you can skip this step and upload it later.
          </Trans>
        </p>
        <br />
        <ul className="form">
          <li>
            <label htmlFor="license-upload">
              <Trans>Upload License</Trans>
            </label>
            <input
              type="file"
              id="license-upload"
              accept=".genesisnetworklicense"
              onChange={event => setFile(event.target.files[0])}
            />
          </li>
          <li>
            <Trans>Your site id is:</Trans> <strong>{site.current ? getConfigItem(site.current, 'SiteId')?.value : ''}</strong>
            <HelpIcon text={t`The site ID is required when requesting a new license file.`} />
          </li>
          <li>
            <button onClick={finish}>
              <Trans>Skip</Trans>
            </button>
            <button
              className={`primary ${savingLicense ? 'processing' : ''}`}
              disabled={!file || savingLicense}
              onClick={handleUpload}
            >
              <Trans>Upload</Trans>
            </button>
          </li>
        </ul>
      </section>
    </div> : null}
  </>);
}

export default GetStarted;
